import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Book, Plus } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { VoiceTraining } from '@/components/VoiceTraining';
import { BookList } from '@/components/BookList';
import { ChapterEditor } from '@/components/ChapterEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const BookCreation = () => {
  const [books, setBooks] = useState([]);
  const [selectedBook, setSelectedBook] = useState(null);
  const [showNewBook, setShowNewBook] = useState(false);
  const [newBook, setNewBook] = useState({ title: '', author: '', genre: 'spiritual' });
  const [loading, setLoading] = useState(false);
  const [generatingCover, setGeneratingCover] = useState(false);

  const loadBooks = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/books`);
      setBooks(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === "development") console.error('Error loading books:', error);
    }
  }, []);

  useEffect(() => {
    loadBooks();
  }, [loadBooks]);

  const createBook = async (e) => {
    e.preventDefault();
    if (!newBook.title || !newBook.author) return;
    try {
      const response = await axios.post(`${API}/books`, newBook);
      toast.success('Book created!');
      setBooks([response.data, ...books]);
      setNewBook({ title: '', author: '', genre: 'spiritual' });
      setShowNewBook(false);
      setSelectedBook(response.data);
    } catch (error) {
      toast.error('Failed to create book');
    }
  };

  const createChapter = async (chapterData) => {
    if (!selectedBook || !chapterData.title) return;
    setLoading(true);
    try {
      await axios.post(`${API}/books/${selectedBook.id}/chapters`, { ...chapterData, book_id: selectedBook.id });
      toast.success('Chapter created with AI!');
      const bookResponse = await axios.get(`${API}/books/${selectedBook.id}`);
      setSelectedBook(bookResponse.data);
    } catch (error) {
      toast.error('Failed to create chapter');
    } finally {
      setLoading(false);
    }
  };

  const exportBook = async (format) => {
    if (!selectedBook) return;
    try {
      const response = await axios.post(`${API}/books/export`, { book_id: selectedBook.id, format }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedBook.title}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Book exported as ${format.toUpperCase()}!`);
    } catch (error) {
      toast.error(`Failed to export as ${format.toUpperCase()}`);
    }
  };

  const updateChapter = async (chapterId, updates) => {
    if (!selectedBook) return;
    try {
      await axios.put(`${API}/books/${selectedBook.id}/chapters/${chapterId}`, updates);
      toast.success('Chapter updated!');
      const bookResponse = await axios.get(`${API}/books/${selectedBook.id}`);
      setSelectedBook(bookResponse.data);
    } catch (error) {
      toast.error('Failed to update chapter');
    }
  };

  const generateCover = async () => {
    if (!selectedBook) return;
    setGeneratingCover(true);
    try {
      await axios.post(`${API}/books/${selectedBook.id}/cover`, { book_id: selectedBook.id });
      toast.success('Book cover generated!');
      const bookResponse = await axios.get(`${API}/books/${selectedBook.id}`);
      setSelectedBook(bookResponse.data);
      loadBooks();
    } catch (error) {
      toast.error('Failed to generate cover');
    } finally {
      setGeneratingCover(false);
    }
  };

  return (
    <div className='h-full flex flex-col bg-black' data-testid='book-creation-panel'>
      <div className='border-b border-border p-6 bg-surface'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-3'>
            <Book size={28} weight='duotone' className='text-foreground' />
            <div>
              <h2 className='text-2xl font-bold tracking-tight' data-testid='books-header-title'>Spiritual Books</h2>
              <p className='text-sm text-muted-foreground' data-testid='books-count'>{books.length} books in your library</p>
            </div>
          </div>
          <Button onClick={() => setShowNewBook(!showNewBook)} className='bg-white text-black hover:bg-gray-200' data-testid='new-book-button'>
            <Plus size={20} weight='bold' className='mr-2' /> New Book
          </Button>
        </div>

        {showNewBook && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className='mt-4 p-4 border border-border bg-surface' data-testid='new-book-form'>
            <form onSubmit={createBook} className='space-y-3'>
              <Input value={newBook.title} onChange={(e) => setNewBook({ ...newBook, title: e.target.value })} placeholder='Book title' className='bg-transparent border-border' data-testid='book-title-input' />
              <Input value={newBook.author} onChange={(e) => setNewBook({ ...newBook, author: e.target.value })} placeholder='Author name' className='bg-transparent border-border' data-testid='book-author-input' />
              <Button type='submit' className='bg-white text-black hover:bg-gray-200' data-testid='create-book-button'>Create Book</Button>
            </form>
          </motion.div>
        )}
      </div>

      <div className='border-b border-border p-4 bg-black'>
        <VoiceTraining />
      </div>

      <div className='flex-1 flex overflow-hidden'>
        <BookList books={books} selectedBook={selectedBook} onSelectBook={setSelectedBook} />
        <div className='flex-1 overflow-y-auto p-6' data-testid='book-editor'>
          <ChapterEditor
            selectedBook={selectedBook}
            loading={loading}
            onCreateChapter={createChapter}
            onUpdateChapter={updateChapter}
            onExportBook={exportBook}
            onGenerateCover={generateCover}
            generatingCover={generatingCover}
          />
        </div>
      </div>
    </div>
  );
};

export default BookCreation;
